/**
 * Global confirm helper — works inside sandboxed iframes where window.confirm()
 * is silently ignored.
 *
 * 1. Mount <GlobalConfirmDialog /> once at the app root.
 * 2. From anywhere call:  await appConfirm('Are you sure?', 'desc?', { destructive: true })
 *    → resolves to true on confirm, false on cancel.
 *
 * Falls back to native window.confirm() if the dialog hasn't mounted yet
 * (e.g. during SSR / very early app init).
 */
import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  destructive: false,
};

// Module-level state shared between appConfirm() and the dialog component.
let registry = {
  show: null,        // installed by GlobalConfirmDialog on mount
};

export function appConfirm(title, description, opts = {}) {
  if (!registry.show) {
    // Fallback to native — may be silently ignored in sandboxed iframes,
    // but better than crashing.
    try {
      const text = description ? `${title}\n\n${description}` : title;
      // eslint-disable-next-line no-alert
      return Promise.resolve(window.confirm(text));
    } catch {
      return Promise.resolve(true);
    }
  }
  return new Promise((resolve) => {
    registry.show({
      ...DEFAULTS,
      title: title || DEFAULTS.title,
      description: description || '',
      confirmText: opts.confirmText || DEFAULTS.confirmText,
      cancelText: opts.cancelText || DEFAULTS.cancelText,
      destructive: !!opts.destructive,
      resolve,
    });
  });
}

// Make it reachable from non-module code if needed (e.g. legacy snippets).
if (typeof window !== 'undefined') {
  window.appConfirm = appConfirm;
}

export function GlobalConfirmDialog() {
  const [state, setState] = useState(null);

  useEffect(() => {
    registry.show = (cfg) => setState(cfg);
    return () => { registry.show = null; };
  }, []);

  if (!state) return null;

  const close = (value) => {
    state.resolve(value);
    setState(null);
  };

  return (
    <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent data-testid="app-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title}</AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription className="whitespace-pre-line">
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)} data-testid="app-confirm-cancel">
            {state.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close(true)}
            data-testid="app-confirm-ok"
            className={state.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
          >
            {state.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
